'use strict';

(() => {
  const SELECTORS = {
    section: '[data-faq-section]',
    item: '[data-faq-item]',
    toggle: '[data-faq-toggle]',
    answer: '[data-faq-answer]'
  };

  const registry = new WeakMap();

  const setItemState = (item, shouldOpen) => {
    const button = item.querySelector(SELECTORS.toggle);
    const answer = item.querySelector(SELECTORS.answer);

    if (!button || !answer) {
      return;
    }

    const resolved = Boolean(shouldOpen);

    item.classList.toggle('is-open', resolved);
    item.dataset.open = resolved ? 'true' : 'false';
    button.setAttribute('aria-expanded', resolved ? 'true' : 'false');
    answer.setAttribute('aria-hidden', resolved ? 'false' : 'true');
    answer.hidden = !resolved;
  };

  const createController = (section) => {
    const items = Array.from(section.querySelectorAll(SELECTORS.item));

    if (!items.length) {
      return null;
    }

    const listeners = [];

    const controller = {
      toggleItem(targetItem, forceState) {
        if (!targetItem || !section.contains(targetItem)) {
          return;
        }

        const shouldOpen =
          typeof forceState === 'boolean' ? forceState : targetItem.dataset.open !== 'true';

        setItemState(targetItem, shouldOpen);
      },
      openItem(targetItem) {
        controller.toggleItem(targetItem, true);
      },
      destroy() {
        listeners.forEach((removeListener) => removeListener());
        listeners.length = 0;
      }
    };

    items.forEach((item) => {
      const button = item.querySelector(SELECTORS.toggle);
      const answer = item.querySelector(SELECTORS.answer);

      if (!button || !answer) {
        return;
      }

      const initiallyOpen = item.dataset.open === 'true' || item.classList.contains('is-open');
      setItemState(item, initiallyOpen);

      const handleClick = () => controller.toggleItem(item);
      button.addEventListener('click', handleClick);
      listeners.push(() => button.removeEventListener('click', handleClick));
    });

    return controller;
  };

  const ensureController = (section) => {
    if (registry.has(section)) {
      return registry.get(section);
    }

    const controller = createController(section);

    if (controller) {
      registry.set(section, controller);
    }

    return controller;
  };

  const initSections = (root = document) => {
    const sections = root.querySelectorAll(SELECTORS.section);

    sections.forEach((section) => {
      ensureController(section);
    });
  };

  const destroySections = (root) => {
    const sections = root.querySelectorAll(SELECTORS.section);

    sections.forEach((section) => {
      const controller = registry.get(section);

      if (!controller) {
        return;
      }

      controller.destroy();
      registry.delete(section);
    });
  };

  const initialize = () => {
    initSections();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySections(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest(SELECTORS.item);

    if (!item) {
      return;
    }

    const section = item.closest(SELECTORS.section);

    if (!section) {
      return;
    }

    const controller = ensureController(section);
    controller?.openItem(item);
  });
})();
